import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ObserversModule } from '@angular/cdk/observers';
import { TranslateModule } from '@ngx-translate/core';
import { ObjectAssociationType, ObjectType, RecordChangeType } from '../../../tokens-and-interfaces';
import { TranslationModule } from '../../../i18n/translation-module';
import { VALID_RECORD_CHANGE_TYPES } from '../../../constants';
import { GalaxyEmptyStateModule } from '@vendasta/galaxy/empty-state';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { MatIconModule } from '@angular/material/icon';
import { ActivityInterface } from '@vendasta/crm';
import { CrmActivityTemplateComponent } from '../activities/activity-template.component';
import { CrmActivityLoggedNoteComponent } from '../activities/logged-note.component';
import { RxState } from '@rx-angular/state';
import { map, combineLatest, filter, distinctUntilChanged } from 'rxjs';
import { ActivitiesTimelineService, ActivityExtended, ActivitySectionInterface } from './timeline.service';
import { CrmActivityRecordChangeComponent } from '../activities/record-change.component';
import { CrmFieldService, StandardIds, StandardExternalIds } from '../../../shared-services/crm-services/field.service';
import { CrmIsTaskOverduePipe } from '../../../shared-pipes/is-task-overdue.pipe';
import { CrmActivityLoggedEmailComponent } from '../activities/logged-email.component';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatInputModule } from '@angular/material/input';
import { ReactiveFormsModule } from '@angular/forms';
import { CrmActivityLoggedCallComponent } from '../activities/logged-call.component';
import { CrmActivityLoggedMeetingComponent } from '../activities/logged-meeting.component';
import { CrmActivityNoteComponent } from '../activities/note.component';
import { CrmActivityEmailComponent } from '../activities/email.component';
import { GalaxyFilterInterface } from '@vendasta/galaxy/filter/chips';
import { SubscriptionList } from '@vendasta/rx-utils';
import { CrmActivityTaskComponent } from '../activities/task.component';
import { CrmActivityAssociationPopoverComponent } from '../activities/association-popover.component';
import { DateFormat } from '@vendasta/galaxy/utility/date-utils';
import { CrmActivityEffectiveDatePipe } from '../../../shared-pipes/activity-effective-date.pipe';
import { CrmActivityLoggerComponent } from '../activities/activity-logger.component';
import { CrmActivityTemplateOptionsDirective } from '../activities/activity-template-options.directive';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { CrmTimelineAssociatedObjectPipe } from './timeline-associated-object.pipe';
import { CrmActivityLoggedCommunicationComponent } from '../activities/logged-communication.component';
import { CrmActivityCommunicationComponent } from '../activities/activity-communication.component';
import { CrmActivityCallComponent } from '../activities/call.component';
import { CrmActivityOpportunityUpdatedComponent } from '../activities/opportunity-change.component';

@Component({
  selector: 'crm-timeline',
  templateUrl: './timeline.component.html',
  styleUrls: ['./timeline.component.scss'],
  providers: [RxState],
  imports: [
    CommonModule,
    TranslateModule,
    TranslationModule,
    GalaxyEmptyStateModule,
    GalaxyLoadingSpinnerModule,
    MatIconModule,
    CrmActivityTemplateComponent,
    CrmActivityLoggedNoteComponent,
    ObserversModule,
    CrmActivityRecordChangeComponent,
    CrmActivityOpportunityUpdatedComponent,
    CrmActivityLoggedEmailComponent,
    CrmActivityLoggedCallComponent,
    CrmActivityLoggedMeetingComponent,
    CrmActivityEmailComponent,
    CrmActivityLoggedCommunicationComponent,
    GalaxyFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    CrmActivityNoteComponent,
    CrmActivityTaskComponent,
    CrmActivityEffectiveDatePipe,
    CrmIsTaskOverduePipe,
    CrmActivityLoggerComponent,
    CrmActivityTemplateOptionsDirective,
    MatMenuModule,
    MatButtonModule,
    CrmTimelineAssociatedObjectPipe,
    CrmActivityCommunicationComponent,
    CrmActivityCallComponent,
    CrmActivityOpportunityUpdatedComponent,
    CrmActivityAssociationPopoverComponent,
  ],
  standalone: true,
})
export class CrmTimelineComponent implements OnDestroy, OnInit {
  @Input({ required: true })
  set crmObjectId(crmObjectId: string) {
    this.activitiesTimelineService.crmObjectId$$.next(crmObjectId);
  }

  @Input({ required: true })
  set objectType(objectType: ObjectType | ObjectAssociationType) {
    this.activitiesTimelineService.objectType$$.next(objectType);
  }

  @Output() activitiesChanged = new EventEmitter<ActivityInterface[]>();

  readonly dateFormat = DateFormat.medium;
  readonly now = new Date();

  crmIdentifiers$ = combineLatest([
    this.activitiesTimelineService.crmObjectId$$.asObservable(),
    this.activitiesTimelineService.objectType$$.asObservable(),
  ]).pipe(map(([crmObjectId, objectType]) => ({ crmObjectId, objectType })));
  activitySections$ = this.activitiesTimelineService.state.select('activitySections');
  uniqueUsers$ = this.activitiesTimelineService.state.select('uniqueUsers');
  readonly filters$ = this.activitiesTimelineService.state.select('filters');
  editingActivities: { [activityId: string]: boolean } = {};

  showInfiniteScroll$ = this.activitiesTimelineService.state
    .select('activities')
    .pipe(map((activities) => activities.length > 0));
  loading$ = combineLatest([
    this.activitiesTimelineService.state.select('activitiesLoading'),
    this.activitiesTimelineService.state.select('activitiesLoadMore'),
  ]).pipe(map(([activitiesLoading, activitiesLoadMore]) => activitiesLoading || activitiesLoadMore));
  filtersPristine$ = combineLatest([
    this.activitiesTimelineService.state.select('searchTerm'),
    this.activitiesTimelineService.state.select('filters'),
  ]).pipe(
    map(([searchTerm, filters]) => {
      if (searchTerm.length !== 0) {
        return false;
      }
      if (filters.length !== 0) {
        return false;
      }
      return true;
    }),
  );
  showLoadingAfterActivities$ = combineLatest([this.loading$, this.filtersPristine$]).pipe(
    map(([loading, filtersPristine]) => loading && filtersPristine),
  );
  showLoadingReplacingActivities$ = combineLatest([this.loading$, this.filtersPristine$]).pipe(
    map(([loading, filtersPristine]) => loading && !filtersPristine),
  );

  subscriptions = SubscriptionList.new();

  constructor(
    private readonly activitiesTimelineService: ActivitiesTimelineService,
    private readonly fieldService: CrmFieldService,
  ) {}

  ngOnInit() {
    this.setupSplitActivitiesIntoSections();
    this.activitiesTimelineService.state.connect('activitySections', this.activitySections$);
    this.activitiesTimelineService.state
      .select('activitiesChanged')
      .pipe(distinctUntilChanged(), filter(Boolean))
      .subscribe(() => {
        this.activitiesTimelineService.state.set({ activitiesChanged: false });
        this.activitiesChanged.emit(this.activitiesTimelineService.state.get('activities'));
      });
  }

  ngOnDestroy() {
    this.subscriptions.destroy();
  }

  isActivityValid(activity: ActivityInterface) {
    if (activity === null || activity === undefined) {
      return false;
    }

    switch (activity.crmObjectSubtype) {
      case 'Email':
        return true;
      case 'Note':
        return (
          this.fieldService.getFieldValueFromCrmObject(activity, StandardIds.ActivityNoteTitle)?.stringValue ||
          this.fieldService.getFieldValueFromCrmObject(activity, StandardIds.ActivityNoteBody)?.stringValue
        );
      case 'Meeting':
        return true;
      case 'Call':
        return true;
      case 'RecordChange':
        return VALID_RECORD_CHANGE_TYPES.includes(
          this.fieldService.getFieldValueFromCrmObject(activity, StandardExternalIds.ActivityRecordChangeType)
            ?.stringValue as RecordChangeType,
        );
      case 'Task':
        return true;
      case 'Communication':
        return true;
      case 'OpportunityChange':
        return true;
      default:
        console.error(`Unexpected activity type: ${activity.crmObjectSubtype}`);
        return false;
    }
  }

  setFilters(filters: GalaxyFilterInterface[]) {
    this.activitiesTimelineService.state.set({ filters: filters });
    this.activitiesTimelineService.reloadActivities();
  }

  setSearchTerm(searchTerm: string) {
    // set searchTerm imperatively since we want it available with the next reload
    this.activitiesTimelineService.state.set({ searchTerm: searchTerm });
    this.activitiesTimelineService.reloadActivities();
  }

  setupSplitActivitiesIntoSections(): void {
    this.activitySections$ = combineLatest([
      this.activitiesTimelineService.state.select('activities'),
      this.activitiesTimelineService.state.select('uniqueUsers'),
    ]).pipe(
      map(([activities, uniqueUsers]) => {
        return activities.map((activity) => {
          return {
            ...activity,
            ownerUser: uniqueUsers.find((user) => user.userId === activity.ownerId),
            isLoggedActivity:
              this.fieldService.getFieldValueFromCrmObject(activity, StandardIds.ActivityManuallyLogged)
                ?.booleanValue ?? false,
          } as ActivityExtended;
        });
      }),
      map((activitiesWithOwnerUser) => this.activitiesTimelineService.getActivitySections(activitiesWithOwnerUser)),
    );
  }

  addActivity(activity: ActivityInterface): void {
    this.activitiesTimelineService.state.set({
      activities: [activity, ...this.activitiesTimelineService.state.get('activities')],
    });
  }

  updateActivity(activity: ActivityInterface): void {
    const activities = this.activitiesTimelineService.state.get('activities');
    const index = activities.findIndex((a) => a.crmObjectId === activity.crmObjectId);
    if (index >= 0) {
      this.activitiesTimelineService.lockActivityEffectiveDate(activities[index]);
      activities[index] = activity;
      this.activitiesTimelineService.state.set({ activities: [...activities] });
      if (activity.crmObjectId) {
        this.editingActivities[activity.crmObjectId] = false;
      }
    }
  }

  loadMoreActivities() {
    this.activitiesTimelineService.loadMoreActivities();
  }

  trackActivitySectionsBy(index: number, section: ActivitySectionInterface) {
    return `${index}-${section.name}`;
  }

  trackActivitiesBy(index: number, activity: ActivityInterface) {
    return `${index}-${activity.crmObjectId}`;
  }
}
